import { formatDate, NgClass } from "@angular/common";
import { Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PonderTasksClient } from "@apiserver-rest/ponder-tasks-client";
import { GetTaskRunEventsResponse, GetTaskRunResponse, TaskEvent, TaskEventType, TaskRun, TaskStatusType } from "@apiserver-rest/ponder-tasks-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { FormatDatePipe } from "@ponder-shared/pipes/format-date.pipe";
import { ColorDictionary } from "@ponder-shared/utils/colors";
import { prettifyJson } from "@ponder-shared/utils/prettifyJson";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { Subject, Subscription, switchMap, take, takeUntil, timer } from "rxjs";

@Component({
    selector: "task-view",
    imports: [NgClass, LoadingComponent, FormatDatePipe],
    templateUrl: "./task-view.component.html"
})
export class TaskViewComponent {
    @ViewChild("modalPanel") private modalPanelRef!: ElementRef<HTMLDivElement>;
    @ViewChild("logContainer") private logContainerRef!: ElementRef<HTMLDivElement>;
    @ViewChild("taskParameters") private taskParametersRef!: ElementRef<HTMLDivElement>;

    public taskId?: string;
    public taskRun?: TaskRun;
    public taskStatus?: TaskStatusType;

    public isVisible: boolean = false;
    public isLoading: boolean = false;
    public loadingSuccess: boolean = false;

    public showLogs: boolean = true;

    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    private viewTaskSub!: Subscription;

    public constructor(
        private ponderTasksClient: PonderTasksClient, 
        private events: EventBroker,
        private route: ActivatedRoute,
        private router: Router) {
            
        this.viewTaskSub = this.events.admin.viewTaskEvent.subscribe((taskId: string | undefined) => {
            this.taskId = taskId;

            if (this.taskId) {
                this.onOpen();
            } else {
                this.onClose();
            }
        });
    }

    public ngOnDestroy(): void {
        this.viewTaskSub?.unsubscribe();
    }

    public onOpen(): void {
        this.stopPolling = new Subject();

        this.setUrlParameters();
        this.fetchData();
        this.pollForEvents();

        this.isVisible = true;
    }

    public onClose(): void {
        this.stopPolling.next(null);
        this.clearUrlParameters();
        
        this.taskId = undefined;
        this.taskStatus = undefined;
        this.showLogs = true;

        this.logContainerRef.nativeElement.innerText = "";
        this.taskParametersRef.nativeElement.innerText = "";

        this.isVisible = false;
    }

    private fetchData(): void {
        if (this.isLoading || !this.taskId) {
            return;
        }

        this.isLoading = true;
        this.loadingSuccess = false;

        this.ponderTasksClient
            .tasksRunGet(this.taskId)
            .pipe(take(1))
            .subscribe({
                next: (response: GetTaskRunResponse) => {
                    if (response.success) {
                        this.taskRun = response.taskRun;
                        this.loadingSuccess = true;
                        this.pollForEvents();
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private stopPolling = new Subject();

    private pollForEvents(): void {
        if (!this.taskId || !this.taskRun) {
            return;
        }

        // todo: need to update the endpoint to get messages since X so we only get the new messages. seems less wasteful that way.
        timer(1, 1000)
                .pipe(
                    switchMap(() => this.ponderTasksClient.tasksRunEvents(this.taskId)),
                    takeUntil(this.stopPolling))
                .subscribe({
                    next: (response: GetTaskRunEventsResponse) => {
                        if (response.status) {
                            this.taskStatus = response.status as string as TaskStatusType;
                            this.updateTaskLog(response.events);

                            if (this.isFinalStatus(this.taskStatus)) {
                                this.stopPolling.next(null);
                            }
                        }
                    }
                })
    }

    private isFinalStatus(status: TaskStatusType): boolean {
        return (status == TaskStatusType.Success || status == TaskStatusType.Failure || status == TaskStatusType.Cancelled);
    }

    public getStatusColor(): string {
        switch (this.taskStatus) {
            case TaskStatusType.Running:
                return ColorDictionary.StatusBlue;

            case TaskStatusType.Success:
                return ColorDictionary.StatusGreen;

            case TaskStatusType.Failure:
                return ColorDictionary.StatusRed;

            case TaskStatusType.Cancelled:
                return ColorDictionary.StatusOrange;

            default:
                return ColorDictionary.StatusGray;
        }
    }

    public onLogsClicked(): void {
        this.showLogs = true;
    }

    public onParametersClicked(): void {
        if (this.showLogs) {
            if (this.taskRun && this.taskParametersRef) {
                this.taskParametersRef.nativeElement.innerText = prettifyJson(this.taskRun.taskProperties);
            }

            this.showLogs = false;
        }
    }

    private updateTaskLog(events: TaskEvent[] | undefined): void {
        var log = "";

        if (events?.length) {
            events.forEach(e => {
                var eventLine = `<span class="text-slate-400">[${formatDate(e.at, "yyyy-MM-dd hh:mm:ss.SSS", "en-US", "utc")}]</span> ${e.message}`;

                switch (e.type) {
                    case TaskEventType.Error:
                        eventLine = `<span class="text-red-600">${eventLine}</span>`;
                        break;

                    case TaskEventType.Error:
                        eventLine = `<span class="text-amber-600">${eventLine}</span>`;
                        break;

                    case TaskEventType.Info:
                    default:
                        break;
                }

                log += eventLine + `<br/>`;
            });
        } else {
            if (this.taskStatus == TaskStatusType.Pending) {
                log = "...";
            } else {
                log = "No event messages found. That's odd.";
            }
        }

        this.logContainerRef.nativeElement.innerHTML = log;
    }

    private setUrlParameters(): void {
        this.router.navigate([], {
            relativeTo: this.route, 
            queryParams: {
                ...this.taskId && { task: this.taskId }
            }
        });
    }

    private clearUrlParameters(): void {
        this.router.navigate([], { relativeTo: this.route });
    }
    
    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        if (!this.isVisible) {
            return;
        }

        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (!this.isVisible) {
            return;
        }
        
        if (!this.modalPanelRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        if (!this.isVisible) {
            return;
        }
        
        // Did the user click outside of our modal panel?
        if (this.mouseDownOutside && !this.modalPanelRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }
}